import { useEffect, useState } from "react";
import { AuthControllerService, TokenRefreshRequest } from "@/api";
import useMutation from "@/hooks/useMutation";
import dateFNS from "@/libs/date";
import { logOut } from "@/libs/store";
import { getTokens, storeTokens } from "@/pages/login/useLogin";

type RefreshTokenResponse = {
  data?: {
    token: string;
    tokenExpiryTime: string;
    refreshToken: string;
  };
};

const logoutInterval = 5 * 60 * 1000;
let logoutTimeoutId: NodeJS.Timeout | null = null;

const useRefreshToken = (onError?: (message: string) => void) => {
  const [refreshState, setRefreshState] = useState(getTokens().tokenExpiryTime);

  const { mutate } = useMutation<
    { tokenRequest: TokenRefreshRequest },
    RefreshTokenResponse
  >(
    async ({ tokenRequest }) => {
      const res = await AuthControllerService.refreshtokenUsingPost({
        tokenRequest,
      });
      // map the underlying HTTP response to the expected RefreshTokenResponse shape
      return {
        data: res.data as
          | {
              token: string;
              tokenExpiryTime: string;
              refreshToken: string;
            }
          | undefined,
      };
    },
    {
      onSuccess: ({ data }) => {
        if (logoutTimeoutId) clearTimeout(logoutTimeoutId);
        if (!data) return;
        storeTokens(data.token, data.tokenExpiryTime, data.refreshToken);
        setRefreshState(data.tokenExpiryTime);
      },
      onError: () => {
        // showing message when it will not work on retry
        onError?.("Session will terminate in the next 1 minute.");
        // instead of logout user we are showing message that session will terminate
        // call logout function after 5 minutes so that user can logout from all sessions
        if (logoutTimeoutId) clearTimeout(logoutTimeoutId);
        logoutTimeoutId = setTimeout(() => {
          void logOut();
        }, logoutInterval);
      },
      retry: 2, // it will try total 3 times
      retryDelay: 10000,
    },
  );

  useEffect(() => {
    const useRefreshTokenIntervalId = setInterval(() => {
      const { tokenExpiryTime, refreshToken } = getTokens();
      const differenceInSeconds = dateFNS.differenceInSeconds(
        new Date(tokenExpiryTime as string),
        Date.now(),
      );

      if (differenceInSeconds < 120) {
        clearInterval(useRefreshTokenIntervalId);
        mutate({ tokenRequest: { refreshToken } }); // calling refresh token
      }
    }, 40000);

    return () => {
      clearInterval(useRefreshTokenIntervalId);
    };
  }, [mutate, refreshState]);
};

export default useRefreshToken;
